import { useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Select, SelectItem, Table, TableBody, TableCell,
  TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import type { User, ServiceOrder, ServiceWo, PartsRow, DviItem } from '../../api/types';
import { fetchServiceWo } from '../../api/service';
import { fetchParts } from '../../api/drilldown';
import { fetchDviRecent, fetchDvi } from '../../api/dvi';
import {
  fetchServiceCatalog, postWoJob, createSsJob, createSsTech,
  type ServiceCatalog, type CatalogJob,
} from '../../api/facturacion';
import { canServiceWOPost, canServiceCatalog } from '../../lib/roles';
import { exportReportPdf } from '../../lib/pdf';
import { t } from '../../lib/i18n';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });
const inputCls = 'mt-1 w-full border border-tremor-border rounded px-2 py-1 bg-tremor-background text-tremor-content-strong';

// A cleared numeric input gives Number("")=0, but a mid-edit "-"/"." gives NaN,
// which JSON.stringify turns to null and the Go handler decodes to 0 — a silent
// $0 / 0-hr post reported as success. Coerce every numeric field to a finite
// number so the value can never leave the panel as NaN/null.
const numOrZero = (s: string): number => {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

// One editable labor line the writer is preparing to post. `key` is a stable
// client id (row identity survives edits/removals); everything else maps 1:1 to
// the add-wo-jobs verb params.
interface LaborRow {
  key: string;
  job_code: string;
  tech: string;
  est_hrs: number;
  act_hrs: number;
  price: number;
}

interface PartRow { line: string; part: string; pdesc: string; on_hand: number; qty: number }

/**
 * Preparar facturación — the WO write-back workspace. Three sections:
 *   1. Mano de obra: build labor lines (catalog picker + approved-DVI quick-adds)
 *      and POST them to the open WO (gated service:wo-post + writes_enabled).
 *   2. Refacciones: a print-only pick-list (the counter captures parts in the POS;
 *      paceBI never writes parts).
 *   3. Catálogo: manager-only inline create job / technician (service:catalog).
 * Reads the catalog + the WO for one store; the daemon enforces the invoiced /
 * full-WO refusals, whose messages surface here.
 */
export default function PrepararFacturacion({
  user, sel, orders, onPick,
}: {
  user: User;
  sel: { sid: number; tnum: number } | null;
  orders: ServiceOrder[];
  onPick: (sid: number, tnum: number) => void;
}) {
  const f = t.service.cockpit.facturar;
  const canPost = canServiceWOPost(user);
  const canCatalog = canServiceCatalog(user);

  if (!sel) {
    // Prefer open WOs (wo_status !== 'C'); fall back to all if none look open.
    const uniq = [...new Map(orders.map((o) => [`${o.sid}-${o.tnum}`, o])).values()];
    const open = uniq.filter((o) => o.wo_status !== 'C');
    const list = (open.length ? open : uniq).slice(0, 40);
    return (
      <Card>
        <Text>{f.pickWo}</Text>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
          {list.map((o) => (
            <button key={`${o.sid}-${o.tnum}`} className="text-left text-sm underline"
              onClick={() => onPick(o.sid, o.tnum)}>#{o.tnum} · {o.anum_name}</button>
          ))}
        </div>
      </Card>
    );
  }

  return <Workspace key={`${sel.sid}-${sel.tnum}`} user={user} sel={sel} canPost={canPost} canCatalog={canCatalog} />;
}

function Workspace({
  user, sel, canPost, canCatalog,
}: {
  user: User;
  sel: { sid: number; tnum: number };
  canPost: boolean;
  canCatalog: boolean;
}) {
  const f = t.service.cockpit.facturar;
  const [catalog, setCatalog] = useState<ServiceCatalog | null>(null);
  const [wo, setWo] = useState<ServiceWo | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const loadCatalog = () => {
    fetchServiceCatalog(sel.sid).then(setCatalog).catch((e: Error) => setErr(e.message));
  };
  const loadWo = () => {
    fetchServiceWo(sel.sid, sel.tnum).then(setWo).catch((e: Error) => setErr(e.message));
  };
  // Workspace is keyed by `${sid}-${tnum}` (see PrepararFacturacion), so it
  // remounts per WO and this runs once — reuse the same loaders the child
  // panels call after a write, so read + reload logic stays single-sourced.
  useEffect(() => {
    loadCatalog();
    loadWo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.sid, sel.tnum]);

  if (err) return <Card><Text>{err}</Text></Card>;
  if (!catalog || !wo) return <Card><Text>…</Text></Card>;

  return (
    <div className="space-y-4">
      <Card>
        <Title className="text-base">{f.title} · #{wo.header.tnum} · {wo.header.anum_name}</Title>
        <Text className="text-sm">{wo.header.equip_desc} · {wo.header.equip_plate || '—'}</Text>
        {!user.features?.writes_enabled && <Text className="text-tremor-content text-xs mt-1">{f.readonly}</Text>}
      </Card>

      <LaborPanel user={user} sel={sel} wo={wo} catalog={catalog} canPost={canPost} onPosted={loadWo} />

      <PartsPanel user={user} sel={sel} />

      {canCatalog && <CatalogPanel sel={sel} catalog={catalog} onChanged={loadCatalog} />}
    </div>
  );
}

// ---------------------------------------------------------------- Mano de obra

function LaborPanel({
  user, sel, wo, catalog, canPost, onPosted,
}: {
  user: User;
  sel: { sid: number; tnum: number };
  wo: ServiceWo;
  catalog: ServiceCatalog;
  canPost: boolean;
  onPosted: () => void;
}) {
  const f = t.service.cockpit.facturar;
  const [rows, setRows] = useState<LaborRow[]>([]);
  const [posting, setPosting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [dviSug, setDviSug] = useState<DviItem[]>([]);

  const jobsByCode = useMemo(() => {
    const m: Record<string, CatalogJob> = {};
    for (const j of catalog.jobs) m[j.job_code] = j;
    return m;
  }, [catalog.jobs]);

  // Optional convenience: approved DVI items for this WO become quick-add rows.
  useEffect(() => {
    if (!user.features?.dvi_enabled) return;
    let a = true;
    fetchDviRecent(sel.sid)
      .then(async (list) => {
        const hit = list.find((i) => i.tnum === sel.tnum);
        if (!hit) return;
        const full = hit.items ? hit : await fetchDvi(hit.id);
        if (a) setDviSug((full.items ?? []).filter((i) => i.customer_decision === 'approved'));
      })
      .catch(() => { /* DVI is a best-effort suggestion; ignore failures */ });
    return () => { a = false; };
  }, [user.features?.dvi_enabled, sel.sid, sel.tnum]);

  const addRow = (init?: Partial<LaborRow>) =>
    setRows((r) => [...r, { key: crypto.randomUUID(), job_code: '', tech: '', est_hrs: 1, act_hrs: 1, price: 0, ...init }]);

  const patchRow = (key: string, patch: Partial<LaborRow>) =>
    setRows((r) => r.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const removeRow = (key: string) => setRows((r) => r.filter((row) => row.key !== key));

  const onPickJob = (key: string, code: string) => {
    const j = jobsByCode[code];
    // Prefill the catalog price when the writer hasn't typed one yet.
    setRows((r) => r.map((row) =>
      row.key === key ? { ...row, job_code: code, price: row.price > 0 ? row.price : (j?.cust_rate ?? 0) } : row));
  };

  const postAll = async () => {
    setMsg(null);
    const ready = rows.filter((r) => r.job_code && r.tech);
    if (!ready.length) { setMsg(f.noRows); return; }
    setPosting(true);
    const failed: LaborRow[] = [];
    let firstErr = '';
    for (const r of ready) {
      try {
        const resp = await postWoJob({
          sid: sel.sid, tnum: sel.tnum, job_code: r.job_code, tech: r.tech,
          est_hrs: r.est_hrs, act_hrs: r.act_hrs, price: r.price,
        });
        if (!resp.ok) { failed.push(r); if (!firstErr) firstErr = resp.error || f.postError; }
      } catch (e) {
        failed.push(r); if (!firstErr) firstErr = (e as Error).message;
      }
    }
    setPosting(false);
    // Keep only the rows that failed (or were incomplete); reload WO totals.
    const incomplete = rows.filter((r) => !(r.job_code && r.tech));
    setRows([...incomplete, ...failed]);
    setMsg(firstErr ? `${f.postError}: ${firstErr}` : f.posted);
    onPosted();
  };

  const showPost = canPost && !!user.features?.writes_enabled;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Title className="text-base">{f.laborTitle}</Title>
        <Button variant="secondary" onClick={() => addRow()}>{f.addFromCatalog}</Button>
      </div>
      <Text className="text-sm mt-1">{f.laborHint}</Text>

      {dviSug.length > 0 && (
        <div className="mt-2">
          <Text className="text-xs font-medium">{f.suggestions}</Text>
          <div className="mt-1 flex flex-wrap gap-2">
            {dviSug.map((d) => (
              <button key={d.id} className="text-xs underline text-left"
                onClick={() => addRow({
                  // Only prefill the code if it's a real catalog leaf; a DVI item
                  // may name a job that isn't a billable ss-jobs row, which would
                  // leave the Select blank. Fall back to an empty pick.
                  job_code: d.job_code && jobsByCode[d.job_code] ? d.job_code : '',
                  price: d.est_price || 0,
                })}>
                + {d.name}{d.est_price ? ` · ${fmtMxn.format(d.est_price)}` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <Table className="mt-3">
          <TableHead><TableRow>
            <TableHeaderCell>{f.colJob}</TableHeaderCell>
            <TableHeaderCell>{f.colTech}</TableHeaderCell>
            <TableHeaderCell className="text-right">{f.colEstHrs}</TableHeaderCell>
            <TableHeaderCell className="text-right">{f.colActHrs}</TableHeaderCell>
            <TableHeaderCell className="text-right">{f.colPrice}</TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key}>
                <TableCell>
                  <Select value={r.job_code} onValueChange={(v) => onPickJob(r.key, v)} enableClear={false} aria-label={f.colJob}>
                    <SelectItem value="">{f.pickJob}</SelectItem>
                    {catalog.jobs.map((j) => <SelectItem key={j.job_code} value={j.job_code}>{j.job_code} · {j.job_desc}</SelectItem>)}
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={r.tech} onValueChange={(v) => patchRow(r.key, { tech: v })} enableClear={false} aria-label={f.colTech}>
                    <SelectItem value="">{f.pickTech}</SelectItem>
                    {catalog.techs.map((tc) => <SelectItem key={tc.ss_tech} value={tc.ss_tech}>{tc.name}</SelectItem>)}
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <input type="number" step="0.1" min="0" value={r.est_hrs} aria-label={f.colEstHrs}
                    onChange={(e) => patchRow(r.key, { est_hrs: numOrZero(e.target.value) })} className={`${inputCls} w-20 text-right`} />
                </TableCell>
                <TableCell className="text-right">
                  <input type="number" step="0.1" min="0" value={r.act_hrs} aria-label={f.colActHrs}
                    onChange={(e) => patchRow(r.key, { act_hrs: numOrZero(e.target.value) })} className={`${inputCls} w-20 text-right`} />
                </TableCell>
                <TableCell className="text-right">
                  <input type="number" step="1" min="0" value={r.price} aria-label={f.colPrice}
                    onChange={(e) => patchRow(r.key, { price: numOrZero(e.target.value) })} className={`${inputCls} w-24 text-right`} />
                </TableCell>
                <TableCell className="text-right">
                  <button className="text-xs underline text-red-500" onClick={() => removeRow(r.key)}>{f.remove}</button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {msg && <Text className="text-sm mt-2">{msg}</Text>}

      <div className="flex items-center justify-between mt-3">
        <Text className="text-sm">{f.woTotal}: <span className="tabular-nums">{fmtMxn.format(wo.totals.labor_mxn)}</span></Text>
        {showPost
          ? <Button onClick={postAll} loading={posting} disabled={rows.length === 0}>{posting ? f.posting : f.postLabor}</Button>
          : <Text className="text-tremor-content text-xs">{f.readonly}</Text>}
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ Refacciones

function PartsPanel({ user, sel }: { user: User; sel: { sid: number; tnum: number } }) {
  const f = t.service.cockpit.facturar;
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PartsRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<PartRow[]>([]);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const rows = await fetchParts({ search: q.trim(), sid: sel.sid, limit: 25 });
      setResults(rows);
    } catch { setResults([]); } finally { setSearching(false); }
  };

  const addPart = (p: PartsRow) => {
    setPicked((cur) => {
      if (cur.some((x) => x.line === p.line && x.part === p.part)) return cur;
      return [...cur, { line: p.line, part: p.part, pdesc: p.pdesc, on_hand: p.on_hand, qty: 1 }];
    });
  };
  const setQty = (line: string, part: string, qty: number) =>
    setPicked((cur) => cur.map((x) => (x.line === line && x.part === part ? { ...x, qty } : x)));
  const removePart = (line: string, part: string) =>
    setPicked((cur) => cur.filter((x) => !(x.line === line && x.part === part)));

  const print = () => {
    exportReportPdf({
      title: `${f.printTitle} · #${sel.tnum}`,
      printedBy: user.username,
      columns: [f.colPart, f.colDesc, f.colQty],
      rows: picked.map((p) => [`${p.line}·${p.part}`, p.pdesc, p.qty]),
      numericColumns: [2],
      filename: `surtido_wo_${sel.tnum}.pdf`,
    });
  };

  return (
    <Card>
      <Title className="text-base">{f.partsTitle}</Title>
      <Text className="text-sm mt-1">{f.partsHint}</Text>
      <div className="mt-2 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={f.partsSearch}
          onKeyDown={(e) => { if (e.key === 'Enter') search(); }} aria-label={f.partsSearch}
          className={`${inputCls} flex-1`} />
        <Button variant="secondary" onClick={search} loading={searching}>{searching ? f.partsSearching : f.add}</Button>
      </div>

      {results.length > 0 && (
        <div className="mt-2 max-h-48 overflow-y-auto border border-tremor-border rounded">
          {results.map((p) => (
            <div key={`${p.line}-${p.part}`} className="flex items-center justify-between px-2 py-1 text-sm border-b border-tremor-border last:border-b-0">
              <span>{p.line}·{p.part} · {p.pdesc}</span>
              <span className="flex items-center gap-3">
                <span className="tabular-nums text-tremor-content">{f.colStock}: {p.on_hand}</span>
                <button className="text-xs underline" onClick={() => addPart(p)}>{f.add}</button>
              </span>
            </div>
          ))}
        </div>
      )}

      {picked.length > 0 ? (
        <Table className="mt-3">
          <TableHead><TableRow>
            <TableHeaderCell>{f.colPart}</TableHeaderCell>
            <TableHeaderCell>{f.colDesc}</TableHeaderCell>
            <TableHeaderCell className="text-right">{f.colStock}</TableHeaderCell>
            <TableHeaderCell className="text-right">{f.colQty}</TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
          </TableRow></TableHead>
          <TableBody>
            {picked.map((p) => (
              <TableRow key={`${p.line}-${p.part}`}>
                <TableCell>{p.line}·{p.part}</TableCell>
                <TableCell>{p.pdesc}</TableCell>
                <TableCell className="text-right tabular-nums">{p.on_hand}</TableCell>
                <TableCell className="text-right">
                  <input type="number" min="1" step="1" value={p.qty} aria-label={f.colQty}
                    onChange={(e) => setQty(p.line, p.part, numOrZero(e.target.value))} className={`${inputCls} w-20 text-right`} />
                </TableCell>
                <TableCell className="text-right">
                  <button className="text-xs underline text-red-500" onClick={() => removePart(p.line, p.part)}>{f.remove}</button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : <Text className="text-sm mt-2 text-tremor-content">{f.partsEmpty}</Text>}

      <div className="flex items-center justify-between mt-3">
        <Text className="text-xs text-tremor-content">{f.posNote}</Text>
        <Button variant="secondary" onClick={print} disabled={picked.length === 0}>{f.print}</Button>
      </div>
    </Card>
  );
}

// -------------------------------------------------------------- Catálogo (crear)

function CatalogPanel({
  sel, catalog, onChanged,
}: {
  sel: { sid: number; tnum: number };
  catalog: ServiceCatalog;
  onChanged: () => void;
}) {
  const f = t.service.cockpit.facturar;
  // create-job form
  const [jCode, setJCode] = useState('');
  const [jDesc, setJDesc] = useState('');
  const [jCat, setJCat] = useState('');
  const [jRate, setJRate] = useState(0);
  const [jComm, setJComm] = useState(1);
  const [jLevel, setJLevel] = useState(1);
  const [jTax, setJTax] = useState(false);
  const [jSup, setJSup] = useState(false);
  const [jCharge, setJCharge] = useState(true);
  const [jBusy, setJBusy] = useState(false);
  const [jMsg, setJMsg] = useState<string | null>(null);
  // create-tech form
  const [tCode, setTCode] = useState('');
  const [tName, setTName] = useState('');
  const [tScom, setTScom] = useState(0);
  const [tRate, setTRate] = useState(0);
  const [tBusy, setTBusy] = useState(false);
  const [tMsg, setTMsg] = useState<string | null>(null);

  const submitJob = async () => {
    setJMsg(null); setJBusy(true);
    try {
      const resp = await createSsJob({
        sid: sel.sid, job_code: jCode, job_desc: jDesc, category: jCat,
        taxable: jTax, supplies: jSup, cust_charge: jCharge,
        job_time: 1, job_level: jLevel, cust_rate: jRate, tech_comm: jComm, tech_fixed: 0,
      });
      setJMsg(resp.ok ? f.created : `${f.createError}: ${resp.error || ''}`);
      if (resp.ok) { setJCode(''); setJDesc(''); onChanged(); }
    } catch (e) { setJMsg(`${f.createError}: ${(e as Error).message}`); }
    finally { setJBusy(false); }
  };

  const submitTech = async () => {
    setTMsg(null); setTBusy(true);
    try {
      const resp = await createSsTech({
        sid: sel.sid, ss_tech: tCode, name: tName, scom: tScom,
        rate_hrs: [tRate, 0, 0, 0, 0, 0], rate_pcnt: [0, 0, 0, 0, 0, 0],
      });
      setTMsg(resp.ok ? f.created : `${f.createError}: ${resp.error || ''}`);
      if (resp.ok) { setTCode(''); setTName(''); onChanged(); }
    } catch (e) { setTMsg(`${f.createError}: ${(e as Error).message}`); }
    finally { setTBusy(false); }
  };

  return (
    <Card>
      <Title className="text-base">{f.catalogTitle}</Title>
      <Text className="text-sm mt-1">{f.catalogHint}</Text>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Crear trabajo */}
        <div className="space-y-2">
          <Text className="font-medium text-sm">{f.createJob}</Text>
          <label className="block text-sm">{f.jobCode}
            <input value={jCode} maxLength={4} onChange={(e) => setJCode(e.target.value.toUpperCase())} className={inputCls} /></label>
          <label className="block text-sm">{f.jobDesc}
            <input value={jDesc} onChange={(e) => setJDesc(e.target.value)} className={inputCls} /></label>
          <div className="text-sm">{f.category}
            <Select value={jCat} onValueChange={setJCat} enableClear={true} className="mt-1">
              {catalog.categories.map((c) => <SelectItem key={c.category} value={c.category}>{c.cat_desc || c.category}</SelectItem>)}
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="block text-sm">{f.custRate}
              <input type="number" min="0" value={jRate} onChange={(e) => setJRate(numOrZero(e.target.value))} className={inputCls} /></label>
            <label className="block text-sm">{f.techComm}
              <input type="number" min="1" max="4" value={jComm} onChange={(e) => setJComm(numOrZero(e.target.value))} className={inputCls} /></label>
            <label className="block text-sm">{f.jobLevel}
              <input type="number" min="1" max="6" value={jLevel} onChange={(e) => setJLevel(numOrZero(e.target.value))} className={inputCls} /></label>
          </div>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1"><input type="checkbox" checked={jCharge} onChange={(e) => setJCharge(e.target.checked)} />{f.custCharge}</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={jTax} onChange={(e) => setJTax(e.target.checked)} />{f.taxable}</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={jSup} onChange={(e) => setJSup(e.target.checked)} />{f.supplies}</label>
          </div>
          {jMsg && <Text className="text-sm">{jMsg}</Text>}
          <Button onClick={submitJob} loading={jBusy} disabled={!jCode || !jDesc}>{jBusy ? f.creating : f.create}</Button>
        </div>

        {/* Crear técnico */}
        <div className="space-y-2">
          <Text className="font-medium text-sm">{f.createTech}</Text>
          <label className="block text-sm">{f.techCode}
            <input value={tCode} maxLength={3} onChange={(e) => setTCode(e.target.value.toUpperCase())} className={inputCls} /></label>
          <label className="block text-sm">{f.techName}
            <input value={tName} onChange={(e) => setTName(e.target.value)} className={inputCls} /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-sm">{f.scom}
              <input type="number" min="0" value={tScom} onChange={(e) => setTScom(numOrZero(e.target.value))} className={inputCls} /></label>
            <label className="block text-sm">{f.rateHr}
              <input type="number" min="0" value={tRate} onChange={(e) => setTRate(numOrZero(e.target.value))} className={inputCls} /></label>
          </div>
          {tMsg && <Text className="text-sm">{tMsg}</Text>}
          <Button onClick={submitTech} loading={tBusy} disabled={!tCode || !tName}>{tBusy ? f.creating : f.create}</Button>
        </div>
      </div>
    </Card>
  );
}
